import { useId, useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { RowTitle } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import type { HideableProduct } from "@acto/taskpane/field-sales-2/utils/productHide";

const LIST_IN_FULL_UP_TO = 3;
const ALWAYS_VISIBLE = 2;

const Frame = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[4]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius["38"],
  background: theme.fieldSalesTwo.colors.surface.glassMedium,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
}));

const Names = styled("ul")<{ followsVisibleRows: boolean }>(() => ({
  margin: 0,
  padding: 0,
  listStyle: "none",
  variants: [{ props: { followsVisibleRows: false }, style: { "& > li:first-of-type": { borderTop: "none" } } }],
}));

const NameItem = styled("li")(({ theme }) => ({
  paddingBlock: theme.fieldSalesTwo.spacing[16],
  borderTop: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
}));

const MoreButton = styled("button")(({ theme }) => ({
  width: "100%",
  minHeight: theme.fieldSalesTwo.spacing[48],
  marginBlock: theme.fieldSalesTwo.spacing[8],
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius["38"],
  background: theme.fieldSalesTwo.colors.surface.glassLight,
  appearance: "none",
  WebkitAppearance: "none",
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
}));

interface SelectedProductsSummaryProps {
  products: HideableProduct[];
}

export function SelectedProductsSummary({ products }: SelectedProductsSummaryProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const [expanded, setExpanded] = useState(false);
  const regionId = useId();

  const folds = products.length > LIST_IN_FULL_UP_TO;
  const visible = folds ? products.slice(0, ALWAYS_VISIBLE) : products;
  const folded = folds ? products.slice(ALWAYS_VISIBLE) : [];

  return (
    <Frame>
      <Names followsVisibleRows={false}>
        {visible.map((product) => (
          <NameItem key={product.id}>
            <RowTitle>{product.name}</RowTitle>
          </NameItem>
        ))}
      </Names>
      {folds && (
        <>
          <Collapse open={expanded} id={regionId}>
            <Names followsVisibleRows>
              {folded.map((product) => (
                <NameItem key={product.id}>
                  <RowTitle>{product.name}</RowTitle>
                </NameItem>
              ))}
            </Names>
          </Collapse>
          {/* Unmounting the trigger on expand would drop the keyboard user's focus to the body. */}
          <MoreButton
            type="button"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
            aria-controls={regionId}
          >
            {expanded ? t("showLess") : t("showMore", { count: folded.length })}
          </MoreButton>
        </>
      )}
    </Frame>
  );
}
